import { Printer, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import { AppLogo } from "@/components/Shared";

/**
 * Riepilogo di quanto dichiarato, stampabile o salvabile in PDF dal browser.
 *
 * Perché una pagina e non un PDF generato dal server: un PDF sarebbe una COPIA
 * degli stessi dati personali depositata in un file, che poi va conservato,
 * protetto e cancellato insieme al resto. La pagina si genera dai dati già
 * archiviati, e "Salva come PDF" del browser produce il documento solo se e
 * quando l'interessato lo vuole — sul suo dispositivo, non sui nostri.
 *
 * Il testo delle informazioni sanitarie NON compare: finirebbe in un file
 * scaricato e magari lasciato aperto su un computer di famiglia. Si dichiara
 * soltanto se è stato conferito, che è quanto serve come prova.
 */

const ETICHETTE_RELAZIONE = {
  madre: "Madre", padre: "Padre", tutore: "Tutore",
  altro_esercente: "Altro esercente la responsabilità genitoriale",
  nonno: "Nonno", nonna: "Nonna", zio: "Zio", zia: "Zia",
  fratello: "Fratello", sorella: "Sorella", altro: "Altro",
};

const ETICHETTE_ESIGENZE = {
  senza_glutine: "Senza glutine", senza_lattosio: "Senza lattosio",
  vegetariano: "Vegetariano", altro: "Altro",
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

function Riga({ etichetta, valore }) {
  return (
    <div className="flex gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
      <span className="text-xs font-bold text-slate-500 w-44 shrink-0">{etichetta}</span>
      <span className="text-sm text-slate-800 min-w-0">{valore || "—"}</span>
    </div>
  );
}

function Sezione({ titolo, children }) {
  return (
    <section className="mb-5">
      <h2 className="font-display font-extrabold text-slate-900 text-sm mb-2 pb-1 border-b-2 border-slate-200">
        {titolo}
      </h2>
      {children}
    </section>
  );
}

const ICONE_STATO = {
  prestato: <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />,
  negato: <XCircle className="h-4 w-4 text-red-400 shrink-0" />,
  "non acquisito": <MinusCircle className="h-4 w-4 text-slate-300 shrink-0" />,
};

export default function RiepilogoIscrizione({ dati, mostraStampa = true }) {
  if (!dati) return null;
  const m = dati.minore || {};
  const c = dati.compilante || {};

  return (
    <div className="bg-white rounded-3xl p-6 md:p-8 print:p-0 print:rounded-none" data-testid="riepilogo-iscrizione">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <AppLogo size={44} className="print:hidden" />
          <div>
            <h1 className="font-display text-lg font-extrabold text-slate-900">
              Riepilogo della richiesta di iscrizione
            </h1>
            <p className="text-xs text-slate-500 font-semibold">
              Oratorio Estivo SS. Medici · inviata il {fmt(dati.inviato_il)}
            </p>
          </div>
        </div>
        {mostraStampa && (
          <button type="button" onClick={() => window.print()}
            className="btn-ghost !py-2 text-xs shrink-0 print:hidden" data-testid="riepilogo-stampa-btn">
            <Printer className="h-3.5 w-3.5" /> Stampa o salva in PDF
          </button>
        )}
      </div>

      <Sezione titolo="Chi ha compilato">
        <Riga etichetta="Nome e cognome" valore={`${c.nome || ""} ${c.cognome || ""}`.trim()} />
        <Riga etichetta="In qualità di" valore={ETICHETTE_RELAZIONE[c.relazione] || c.relazione} />
        <Riga etichetta="Telefono" valore={c.telefono} />
      </Sezione>

      <Sezione titolo="Dati del minore">
        <Riga etichetta="Nome e cognome" valore={`${m.nome || ""} ${m.cognome || ""}`.trim()} />
        <Riga etichetta="Data di nascita" valore={m.data_nascita} />
        <Riga etichetta="Codice fiscale" valore={m.codice_fiscale} />
        <Riga etichetta="Taglia maglia" valore={m.taglia_maglia} />
      </Sezione>

      <Sezione titolo="Contatti di emergenza">
        {(dati.contatti_emergenza || []).length === 0 ? (
          <p className="text-sm text-slate-400">Nessun contatto indicato</p>
        ) : (
          dati.contatti_emergenza.map((k, i) => (
            <Riga key={i}
              etichetta={ETICHETTE_RELAZIONE[k.relazione] || "Contatto"}
              valore={`${k.nome || ""} ${k.cognome || ""}`.trim() + (k.telefono ? ` · ${k.telefono}` : "")} />
          ))
        )}
      </Sezione>

      <Sezione titolo="Esigenze e note">
        <Riga etichetta="Esigenze alimentari"
          valore={(dati.esigenze_alimentari || []).map((e) => ETICHETTE_ESIGENZE[e] || e).join(", ")
            + (dati.esigenze_alimentari_altro ? ` (${dati.esigenze_alimentari_altro})` : "")} />
        <Riga etichetta="Note organizzative" valore={dati.note_organizzative} />
        <Riga etichetta="Informazioni sanitarie"
          valore={
            dati.info_sanitarie_scartate
              ? "Conferite ma NON conservate (consenso non prestato)"
              : dati.info_sanitarie_presenti
                ? "Conferite e conservate (il testo non è riportato in questo riepilogo)"
                : "Non conferite"
          } />
      </Sezione>

      <Sezione titolo="Consensi">
        {(dati.consensi || []).map((k) => (
          <div key={k.chiave} className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-b-0">
            {ICONE_STATO[k.stato]}
            <span className="text-sm text-slate-800 flex-1 min-w-0">{k.etichetta}</span>
            <span className={`text-xs font-bold shrink-0 ${
              k.stato === "prestato" ? "text-emerald-600"
                : k.stato === "negato" ? "text-red-500" : "text-slate-400"
            }`}>
              {k.stato === "prestato" ? "Prestato" : k.stato === "negato" ? "Non prestato" : "Non richiesto"}
            </span>
          </div>
        ))}
      </Sezione>

      <Sezione titolo="Informativa">
        <Riga etichetta="Versione" valore={dati.informativa?.versione ? `Versione ${dati.informativa.versione}` : "—"} />
        <Riga etichetta="Presa visione il" valore={fmt(dati.informativa?.at)} />
        <Riga etichetta="Impronta del testo"
          valore={dati.informativa?.hash
            ? <code className="text-[10px] break-all font-mono text-slate-500">{dati.informativa.hash}</code>
            : "—"} />
      </Sezione>

      <p className="text-[11px] text-slate-400 font-semibold leading-relaxed border-t border-slate-200 pt-3">
        L'impronta è il codice che identifica in modo univoco il testo dell'informativa letta:
        permette di verificare, anche a distanza di tempo, quale documento era in vigore.
        {" "}{dati.destinatario_cf}
      </p>
    </div>
  );
}
